import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import '@/App.css';
import { Toaster } from 'sonner';

import Home from './pages/Home';
import CreateEvent from './pages/CreateEvent';
import JoinByLink from './pages/JoinByLink';
import JoinByQuery from './pages/JoinByQuery';
import TripDetail from './pages/TripDetail';
import PlayerManagement from './pages/PlayerManagement';
import CoursePicker from './pages/CoursePicker';
import RoundSetup from './pages/RoundSetup';
import TeamDraw from './pages/TeamDraw';
import ScoringPinGate from './pages/ScoringPinGate';
import Scoring from './pages/Scoring';
import Leaderboard from './pages/Leaderboard';
import ScorecardView from './pages/ScorecardView';
import ScrambleScorecardView from './pages/ScrambleScorecardView';
import LeagueTable from './pages/LeagueTable';
import CostCalculator from './pages/CostCalculator';
import ApproveCapacity from './pages/ApproveCapacity';
import OrgSignup from './pages/OrgSignup';
import OrgLogin from './pages/OrgLogin';
import OrgForgotPassword from './pages/OrgForgotPassword';
import OrgResetPassword from './pages/OrgResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import OrgAccount from './pages/OrgAccount';
import Wizard, { WizardCreated } from './pages/Wizard';
import PaymentReturn from './pages/PaymentReturn';
import OwnerSettings from './pages/OwnerSettings';
import OwnerAddCourse from './pages/OwnerAddCourse';
import OwnerCourseRequests from './pages/OwnerCourseRequests';
import OwnerCoursesList from './pages/OwnerCoursesList';
import TeamsTeeTimes from './pages/TeamsTeeTimes';
import ReviewScorecard from './pages/ReviewScorecard';
import OwnerPromoCodes from './pages/OwnerPromoCodes';
import ExtraCapacityCheckout from './pages/ExtraCapacityCheckout';
import TopupReturn from './pages/TopupReturn';

import OwnerGuardProvider from './components/OwnerGuardProvider';
import UpdateBanner from './components/UpdateBanner';
import UpdatedNotice from './components/UpdatedNotice';
import { LeaderboardErrorBoundary } from './components/LeaderboardErrorBoundary';
import TripAccessRedirectGuard from './components/TripAccessRedirectGuard';
import { AuthProvider } from './lib/authContext';
import { getTrip } from './lib/api';

function ScorecardRoute() {
  const { code, roundId } = useParams();
  const [mode, setMode] = useState(null);
  const check = useCallback(async () => {
    try {
      const res = await getTrip(code);
      const r = res.data.rounds?.find(r => r.id === roundId);
      setMode(r?.scoring_mode === 'texas_scramble' ? 'scramble' : 'individual');
    } catch { setMode('individual'); }
  }, [code, roundId]);
  useEffect(() => { check(); }, [check]);
  if (!mode) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;
  return mode === 'scramble' ? <ScrambleScorecardView /> : <ScorecardView />;
}

import InAppBrowserBanner from './components/InAppBrowserBanner';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <Toaster position="top-center" richColors />
      <OwnerGuardProvider />
      <InAppBrowserBanner />
      <UpdateBanner />
      <UpdatedNotice />
      <TripAccessRedirectGuard />
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<Home />} />
        <Route path="/create" element={<CreateEvent />} />
        <Route path="/join/:code" element={<JoinByLink />} />
        <Route path="/join" element={<JoinByQuery />} />
        <Route path="/signup" element={<OrgSignup />} />
        <Route path="/login" element={<OrgLogin />} />
        <Route path="/organiser/forgot-password" element={<OrgForgotPassword />} />
        <Route path="/reset-password" element={<OrgResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/account" element={<OrgAccount />} />
        <Route path="/wizard/new" element={<Wizard />} />
        <Route path="/wizard/created" element={<WizardCreated />} />
        <Route path="/payment-return" element={<PaymentReturn />} />
        <Route path="/settings" element={<OwnerSettings />} />
        <Route path="/trip/:code" element={<TripDetail />} />
        <Route path="/trip/:code/players" element={<PlayerManagement />} />
        <Route path="/trip/:code/round/:roundId/course" element={<CoursePicker />} />
        <Route path="/trip/:code/round/:roundId/setup" element={<RoundSetup />} />
        <Route path="/trip/:code/round/:roundId/teams" element={<TeamDraw />} />
        <Route path="/trip/:code/round/:roundId/tee-sheet" element={<TeamsTeeTimes />} />
        <Route path="/trip/:code/round/:roundId/pin" element={<ScoringPinGate />} />
        <Route path="/trip/:code/round/:roundId/scoring" element={<Scoring />} />
        <Route path="/trip/:code/round/:roundId/review/:groupIdx" element={<ReviewScorecard />} />
        <Route path="/trip/:code/round/:roundId/leaderboard" element={<LeaderboardErrorBoundary><Leaderboard /></LeaderboardErrorBoundary>} />
        <Route path="/trip/:code/round/:roundId/scorecard" element={<ScorecardRoute />} />
        <Route path="/trip/:code/league" element={<LeagueTable />} />
        <Route path="/trip/:code/upgrade" element={<CostCalculator />} />
        <Route path="/trip/:code/extra-capacity" element={<ExtraCapacityCheckout />} />
        <Route path="/topup-return" element={<TopupReturn />} />
        <Route path="/admin/approve-capacity" element={<ApproveCapacity />} />
        <Route path="/admin/courses" element={<OwnerCoursesList />} />
        <Route path="/admin/add-course" element={<OwnerAddCourse />} />
        <Route path="/admin/course-requests" element={<OwnerCourseRequests />} />
        <Route path="/admin/promo-codes" element={<OwnerPromoCodes />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
